import { ThemeEvents } from '@theme/events';

class FreeShippingThreshold extends HTMLElement {
  connectedCallback() {
    this.messageEl = this.querySelector("#threshold-message");
    this.progressEl = this.querySelector("#threshold-progress");
    this.bgSegments = this.querySelectorAll('.cart__progress-segments_background .cart__progress-segment');
    this.fgSegments = this.querySelectorAll('.cart__progress-segments_foreground .cart__progress-segment');
    this.labelCells = this.querySelectorAll('.cart__progress-cell');

    this.sectionId = this.dataset.sectionId;
    this.initWithRetry();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener("free-shipping-threshold:update", this.onExternalUpdate);
  }

  _hasDependencies() {
    return !!(window.cartThresholdConfig && window.shippingThresholds && window.Shopify?.currency);
  }

  initWithRetry(attempt = 1, maxAttempts = 5) {
    if (this._hasDependencies()) {
      this.init();
    } else if (attempt < maxAttempts) {
      setTimeout(() => this.initWithRetry(attempt + 1, maxAttempts), 50);
    } else {
      console.error('[Free Shipping Threshold] Failed to initialize after', maxAttempts, 'attempts - data still not available');
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener("free-shipping-threshold:update", this.onExternalUpdate);
  }

  onCartUpdate = (event) => {
    this.init(event);
  };

  onExternalUpdate = () => {
    this.init();
  };

  init(event = null) {
    if (this._isInitializing) {
      return;
    }

    this._isInitializing = true;

    if (!this._hasDependencies()) {
      console.warn('[Free Shipping Threshold] Missing dependencies - component will not initialize');
      this._isInitializing = false;
      return;
    }

    const cfg = window.cartThresholdConfig;
    const thresholdData = window.shippingThresholds;
    const { active: activeCurrency } = window.Shopify.currency;

    let raw = thresholdData.markets?.[thresholdData.currentMarket]
           || thresholdData.currencies?.[activeCurrency]
           || { standard: 0, express: 0, gift: 0 };

    const activeTiers = [
      { value: raw.standard, label: cfg.labels.standard },
      { value: raw.express, label: cfg.labels.express },
      { value: raw.gift, label: cfg.labels.gift }
    ].filter(tier => tier.value > 0);

    if (activeTiers.length === 0) {
      if (this.dataset.autoHide === 'true') this.style.display = 'none';
      this._isInitializing = false;
      return;
    }

    this.style.display = '';

    let cartTotal = parseInt(this.dataset.cartTotal || "0", 10);

    if (event?.detail?.resource?.total_price != null) {
      cartTotal = parseInt(event.detail.resource.total_price, 10);
      this.dataset.cartTotal = cartTotal.toString();
    }
    else if (event?.detail?.data?.sections?.[this.sectionId]) {
      const htmlString = event.detail.data.sections[this.sectionId];
      const match = htmlString.match(/data-cart-total=["'](\d+)["']/);

      if (match) {
        cartTotal = parseInt(match[1], 10);
        this.dataset.cartTotal = cartTotal.toString();
      } else {
        console.warn('[Free Shipping Threshold] Could not parse cart total from section HTML');
      }
    }

    this.updateProgressBar(activeTiers, cartTotal);
    this._isInitializing = false;
  }

  updateProgressBar(activeTiers, cartTotal) {
    const tierCount = activeTiers.length;
    this.style.setProperty('--cols', tierCount);

    const updateVisibility = (el, i) => el.style.display = i < tierCount ? '' : 'none';
    this.bgSegments.forEach(updateVisibility);
    this.fgSegments.forEach(updateVisibility);

    this.labelCells.forEach((label, i) => {
      if (i < tierCount) {
        label.style.display = '';
        const textSpan = label.querySelector('span:last-child');
        if (textSpan) textSpan.textContent = activeTiers[i].label;
      } else {
        label.style.display = 'none';
      }
    });

    const progress = Math.min(this.calculateProgress(cartTotal, activeTiers), 100);
    this.style.setProperty('--width', `${progress}%`);

    if (this.progressEl) {
      this.progressEl.setAttribute('aria-valuenow', Math.round(progress).toString());
    }

    this.updateStepClassAndMessage(cartTotal, activeTiers);
  }

  updateStepClassAndMessage(cartTotal, activeTiers) {
    this.classList.remove('m-step_1', 'm-step_2', 'm-step_3');

    let stepIndex = -1;
    for (let i = 0; i < activeTiers.length; i++) {
      if (cartTotal >= activeTiers[i].value) stepIndex = i;
      else break;
    }

    let message = '';

    if (stepIndex === activeTiers.length - 1) {
      this.classList.add(`m-step_${activeTiers.length}`);
      message = this.buildUnlockedAllMessage(activeTiers);
    } else if (stepIndex >= 0) {
      this.classList.add(`m-step_${stepIndex + 1}`);
      const achievedTier = activeTiers[stepIndex];
      const targetTier = activeTiers[stepIndex + 1];
      const remaining = Math.max(0, targetTier.value - cartTotal);
      message = this.buildProgressMessage(achievedTier, targetTier, remaining);
    } else {
      const targetTier = activeTiers[0];
      const remaining = Math.max(0, targetTier.value - cartTotal);
      message = this.buildDefaultMessage(targetTier, remaining);
    }

    if (this.messageEl) this.messageEl.innerHTML = message;
  }

  buildDefaultMessage(targetTier, remaining) {
    const amount = this.formatMoney(remaining);
    const template = window.cartThresholdConfig?.messages?.spendToUnlock ||
                    'Spend <strong>{{ amount }}</strong> more to unlock {{ benefit }}.';
    return template
      .replace('{{ amount }}', amount)
      .replace('{{ benefit }}', targetTier.label);
  }

  buildProgressMessage(achievedTier, targetTier, remaining) {
    const amount = this.formatMoney(remaining);
    const template = window.cartThresholdConfig?.messages?.unlockedProgress ||
                    "You've unlocked {{ achieved }}! Spend <strong>{{ amount }}</strong> more to get {{ next }}.";
    return template
      .replace('{{ achieved }}', achievedTier.label)
      .replace('{{ amount }}', amount)
      .replace('{{ next }}', targetTier.label);
  }

  buildUnlockedAllMessage(activeTiers) {
    if (activeTiers.length === 1) {
      const template = window.cartThresholdConfig?.messages?.unlockedOne ||
                      "You've unlocked {{ benefit }}!";
      return template.replace('{{ benefit }}', activeTiers[0].label);
    } else if (activeTiers.length === 2) {
      const template = window.cartThresholdConfig?.messages?.unlockedTwo ||
                      "You've unlocked {{ benefit1 }} and {{ benefit2 }}!";
      return template
        .replace('{{ benefit1 }}', activeTiers[0].label)
        .replace('{{ benefit2 }}', activeTiers[1].label);
    } else {
      const labels = activeTiers.map(t => t.label);
      const lastLabel = labels.pop();
      const benefits = `${labels.join(', ')}, and ${lastLabel}`;
      const template = window.cartThresholdConfig?.messages?.unlockedAll ||
                      "You've unlocked {{ benefits }}!";
      return template.replace('{{ benefits }}', benefits);
    }
  }

  calculateProgress(cartTotal, activeTiers) {
    const tierCount = activeTiers.length;
    if (tierCount === 0) return 0;

    const maxThreshold = activeTiers[tierCount - 1].value;
    if (cartTotal >= maxThreshold) return 100;

    const segmentWidth = 100 / tierCount;

    for (let i = 0; i < tierCount; i++) {
      const tier = activeTiers[i];
      if (cartTotal < tier.value) {
        const prevValue = i === 0 ? 0 : activeTiers[i - 1].value;
        const baseProgress = i * segmentWidth;
        const currentSegmentProgress = (cartTotal - prevValue) / (tier.value - prevValue);
        return baseProgress + (currentSegmentProgress * segmentWidth);
      }
    }
    return 0;
  }

  formatMoney(cents) {
    const moneyFormat = window.cartThresholdConfig?.moneyFormat || "${{amount}}";
    if (typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    return moneyFormat.replace("{{amount}}", (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 }));
  }
}

if (!customElements.get("free-shipping-threshold")) {
  customElements.define("free-shipping-threshold", FreeShippingThreshold);
}
